import { useCallback, useEffect } from 'react';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { DndProvider } from 'react-dnd';
import { Icon, Button, Field } from '@ontruck/orc';
import RoutingMap, { RoutingMapActions } from 'modules/routing-map';

import { useRouting, ERRORS } from './context';
import { someStopHasDifferentDate } from './context/utils';
import RoutingStop, { RoutingSummaryStop } from './RoutingStop';
import RoutingRoutePlan from './RoutingRoutePlan';
import { useDispatchingUI } from 'modules/dispatching/ui';
import VehicleInfo from 'modules/assignment-cards/components/VehicleInfo';

const ROUTING_MODES = {
  DEFAULT: 'default',
  UPGRADE_OFFER: 'upgrade_offer',
};

const t = {
  create_route: 'Create route',
  cost: 'Cost',
  price: 'Price',
  margin: 'Margin',
  distance: 'Distance',
  create_route_header: 'Create a route',
  upgrade_route_header: 'Upgrade route',
  call_to_action: {
    [ROUTING_MODES.DEFAULT]: 'Create route',
    [ROUTING_MODES.UPGRADE_OFFER]: 'Create upgraded offer',
  },
  call_to_action_assign_upgrade: 'Assign directly',
  discard: {
    [ROUTING_MODES.DEFAULT]: 'Discard route',
    [ROUTING_MODES.UPGRADE_OFFER]: 'Discard upgrade',
  },
  errors: {
    [ERRORS.JUST_ASSIGNED_JOURNEYS]: 'Just assigned and in transit journeys can be upgraded',
    [ERRORS.UNKNOWN]: 'There was an unexpected error',
    default: 'There was an unexpected error',
  },
};

const ENTER_KEY = 13;

const getRoutingMode = ({ journeyForUpgrade }) => {
  if (journeyForUpgrade) {
    return ROUTING_MODES.UPGRADE_OFFER;
  }

  return ROUTING_MODES.DEFAULT;
};

const HeaderComponent = ({ onClick }) => {
  return (
    <div className="header">
      <button className="close" onClick={onClick}>
        <Icon glyph="close" size="1.6rem" color="white" />
      </button>
    </div>
  );
};

const RoutingModuleWithProvider = ({ className, filters }) => {
  const { mutateListFn } = useDispatchingUI();

  const {
    stops,
    moveStop,
    loading,
    routePlan,
    createRoute,
    createRouteAssigned,
    removeStop,
    clear,
    addJourney,
    journeyForUpgrade,
    error,
    clearError,
    mapOpened,
    openMap,
    closeMap,
    addModifiedCost,
    setHoveredStop,
    isStopHovered,
    modifyStopTimes,
    getShipperOrdersForStopIndex,
    mapStopFilter,
    setMapFilter,
    setMutateAfterCreation,
  } = useRouting();

  const handleJourneyInputKeyPress = useCallback(
    (e) => {
      if (e.charCode !== ENTER_KEY) {
        return;
      }

      addJourney(e.target.value);
    },
    [addJourney]
  );

  const handleJourneyInputChange = useCallback(() => {
    clearError();
  }, [clearError]);

  const routingMode = getRoutingMode({ journeyForUpgrade });

  const handleOnHover = useCallback(
    (id) => {
      setHoveredStop(id);
    },
    [setHoveredStop]
  );

  const handleOnHoverLeft = useCallback(() => {
    setHoveredStop(null);
  }, [setHoveredStop]);

  const handleMapFilterChange = useCallback(
    (e) => {
      setMapFilter(e.target.name);
    },
    [setMapFilter]
  );

  const showDateInStops = someStopHasDifferentDate(stops);

  useEffect(() => {
    if (setMutateAfterCreation) {
      setMutateAfterCreation(() => mutateListFn);
    }
  }, [mutateListFn, setMutateAfterCreation]);

  if (!stops.length && !loading) {
    return null;
  }

  const stopLoadType = stops
    .map((stop) => stop.load_types?.toString())
    .toString()
    .split(',');
  const routeLoadType = stopLoadType.filter((loadType, index) => {
    return stopLoadType.indexOf(loadType) === index;
  });
  return (
    <div className={className}>
      <div className="route-container">
        {loading && <div className="fullLoading">&nbsp;</div>}
        <HeaderComponent onClick={clear} />
        <div className="content">
          <div className="stops-header">
            {routingMode === ROUTING_MODES.UPGRADE_OFFER && (
              <>
                {t.upgrade_route_header}{' '}
                <span className="small">
                  {journeyForUpgrade.driver_name} (J{journeyForUpgrade?.id})
                </span>
              </>
            )}
            {routingMode === ROUTING_MODES.DEFAULT && t.create_route_header}
          </div>
          <div className="subheader">
            <div className="vehicle-info">
              <VehicleInfo model={{ vehicle: journeyForUpgrade?.vehicle }} />
            </div>
            <Field
              label="RoutingJourneyField"
              name="RoutingJourneyField"
              id="RoutingJourneyField"
              placeholder="J1234"
              labelType="hidden"
              onKeyPress={handleJourneyInputKeyPress}
              onChange={handleJourneyInputChange}
              type="number"
              disabled={loading || !!journeyForUpgrade}
            />
          </div>
          {!!error && <div className="error">{t.errors[error] || error || t.errors.default}</div>}
          <div className="stops" data-testid="RoutingModuleStops">
            {stops.map((stop, index) => (
              <RoutingStop
                stop={stop}
                key={stop.id}
                id={stop.id}
                index={index}
                moveStop={moveStop}
                onRemove={removeStop}
                isUpgradingJourney={routingMode === ROUTING_MODES.UPGRADE_OFFER}
                onHover={handleOnHover}
                onHoverLeft={handleOnHoverLeft}
                isHovered={isStopHovered && isStopHovered(index)}
                shipperOrders={getShipperOrdersForStopIndex && getShipperOrdersForStopIndex(index)}
                onEditTimes={modifyStopTimes}
                showDate={showDateInStops}
              />
            ))}
            <RoutingSummaryStop
              stop={{
                real_pallets: routePlan?.max_real_pallets,
                load_types: routeLoadType,
                type_pallets: routePlan?.type_pallets,
                total_weight: routePlan?.max_weight,
                equivalent_pallets: routePlan?.max_equivalent_pallets,
                item_height: routePlan?.item_height,
                liftgate_needed: routePlan?.liftgate_needed,
                side_load_needed: routePlan?.side_load_needed,
                topload_needed: routePlan?.topload_needed,
              }}
            />
          </div>
          <div className="footer">
            <div className="summary">
              <RoutingRoutePlan
                routePlan={routePlan}
                onOpenMapClick={openMap}
                onChangeCost={addModifiedCost}
                journeyForUpgrade={journeyForUpgrade}
              />
            </div>
            <div className="actions">
              <Button skin="secondary" nude text={t.discard[routingMode]} onClick={clear} />
              {routingMode === ROUTING_MODES.UPGRADE_OFFER && (
                <Button
                  skin="primary"
                  ghost
                  onClick={createRouteAssigned}
                  disabled={!routePlan}
                  text={t.call_to_action_assign_upgrade}
                />
              )}
              <Button
                skin="primary"
                onClick={createRoute}
                disabled={!routePlan}
                text={t.call_to_action[routingMode]}
              />
            </div>
          </div>
        </div>
      </div>
      {!!mapOpened && (
        <div className="map-container">
          <HeaderComponent onClick={closeMap} />
          <div className="map-content">
            <RoutingMapActions
              stops={stops}
              mapStopFilter={mapStopFilter}
              onFilterChange={handleMapFilterChange}
            />
            <RoutingMap stops={stops} filters={filters} mapStopFilter={mapStopFilter} />
          </div>
        </div>
      )}
    </div>
  );
};

const RoutingModule = (props) => (
  <DndProvider backend={HTML5Backend}>
    <RoutingModuleWithProvider {...props} />
  </DndProvider>
);

export default RoutingModule;
